import { useMemo } from 'react';
import type { Dish, Allergen } from '../../shared/menu';
import { DISH_LOW_CONFIDENCE_THRESHOLD } from '../../shared/menu';
import { priceForDisplay } from '../../shared/currencies';
import { AllergenChip } from './AllergenChip';
import { useT } from '../lib/i18n';

interface DishCardProps {
  dish: Dish;
  currency: string | null;
  filteredAllergens: Allergen[];
  // When true, dishes whose allergenUncertainty intersects the user's hard
  // filters are also flagged (and hidden by default). Default false — opt-in
  // from Settings for users who want to err safe.
  hideUncertain: boolean;
  showFiltered: boolean;
  reverseLanguage: boolean;
  onTap: () => void;
  onQuickAdd: () => void;
  inCart: number;
  // Set by MenuScreen when the *menu* is broadly low-confidence — we suppress
  // the per-dish uncertainty chip because the menu-level strip already covers it.
  suppressUncertainty?: boolean;
}

export function DishCard({ dish, currency, filteredAllergens, hideUncertain, showFiltered, reverseLanguage, onTap, onQuickAdd, inCart, suppressUncertainty }: DishCardProps) {
  const t = useT();
  const triggeringAllergens = useMemo(
    () => dish.allergens.filter((a) => filteredAllergens.includes(a)),
    [dish.allergens, filteredAllergens],
  );
  const triggeringUncertain = useMemo(
    () => (hideUncertain
      ? dish.allergenUncertainty.filter((a) => filteredAllergens.includes(a))
      : []),
    [dish.allergenUncertainty, filteredAllergens, hideUncertain],
  );
  const isFlagged = triggeringAllergens.length > 0 || triggeringUncertain.length > 0;
  const priceDisplay = priceForDisplay({ price: dish.price, priceText: dish.priceText, currency, preferOriginal: reverseLanguage });
  const hasPrice = priceDisplay.length > 0;
  const isUncertain = !suppressUncertainty && dish.confidence < DISH_LOW_CONFIDENCE_THRESHOLD;

  if (isFlagged && !showFiltered) return null;

  const primaryName = reverseLanguage ? dish.nameOriginal : dish.nameTranslated;
  const secondaryName = reverseLanguage ? dish.nameTranslated : dish.nameOriginal;
  const description = reverseLanguage ? dish.descriptionOriginal : dish.descriptionTranslated;

  return (
    <article
      onClick={onTap}
      style={{
        padding: '16px 4px',
        display: 'flex',
        gap: 12,
        cursor: 'pointer',
        borderBottom: '1px solid var(--color-divider)',
        position: 'relative',
        background: isFlagged ? 'rgba(196, 51, 43, 0.04)' : 'transparent',
        borderLeft: isFlagged ? '3px solid var(--color-danger)' : '3px solid transparent',
        paddingLeft: isFlagged ? 12 : 4,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        {isFlagged && (
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              color: 'var(--color-danger)',
              paddingBottom: 4,
            }}
          >
            {triggeringAllergens.length > 0 ? '⚠ Contains' : '⚠ Maybe contains'}{' '}
            {[...triggeringAllergens, ...triggeringUncertain].join(', ')}
          </div>
        )}
        <h3 style={{ fontSize: 17, fontWeight: 500, lineHeight: 1.3, margin: 0 }}>{primaryName}</h3>
        <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', margin: '2px 0 0', lineHeight: 1.35 }}>{secondaryName}</p>
        {description && (
          <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', margin: '6px 0 0', lineHeight: 1.45, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
            {description}
          </p>
        )}
        {dish.culturalContext && (
          <p style={{ fontSize: 12, color: 'var(--color-text-muted)', margin: '6px 0 0', fontStyle: 'italic' }}>
            {dish.culturalContext}
          </p>
        )}
        {(dish.allergens.length > 0 || dish.allergenUncertainty.length > 0 || dish.spiceLevel || isUncertain) && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8, alignItems: 'center' }}>
            {dish.allergens.map((a) => (
              <AllergenChip key={`c-${a}`} allergen={a} variant="confirmed" />
            ))}
            {dish.allergenUncertainty.map((a) => (
              <AllergenChip key={`m-${a}`} allergen={a} variant="maybe" />
            ))}
            {dish.spiceLevel != null && dish.spiceLevel > 0 && (
              <span style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>
                {'🌶'.repeat(dish.spiceLevel)}
              </span>
            )}
            {isUncertain && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  padding: '2px 8px',
                  borderRadius: 9999,
                  border: '1px dashed var(--color-text-secondary)',
                  background: 'transparent',
                  color: 'var(--color-text-secondary)',
                  fontSize: 11,
                  fontWeight: 500,
                }}
              >
                <span aria-hidden style={{ fontSize: 11, fontWeight: 700 }}>?</span>
                Uncertain translation — tap for original
              </span>
            )}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8, paddingTop: 2 }}>
        {hasPrice ? (
          <span className="num" style={{ fontSize: 14, fontWeight: 500, color: 'var(--color-primary)', whiteSpace: 'nowrap' }}>
            {priceDisplay}
          </span>
        ) : (
          <span
            title="Price not shown on menu — ask the server"
            style={{
              fontSize: 11,
              fontWeight: 500,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              color: 'var(--color-text-secondary)',
              border: '1px dashed var(--color-divider)',
              borderRadius: 9999,
              padding: '2px 8px',
              whiteSpace: 'nowrap',
            }}
          >
            {t('dish.priceUnknown')}
          </span>
        )}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onQuickAdd();
          }}
          aria-label={inCart > 0 ? `Add another, ${inCart} in cart` : 'Add to order'}
          style={{
            width: 44,
            height: 44,
            borderRadius: '50%',
            border: 'none',
            background: inCart > 0 ? 'var(--color-primary)' : 'var(--color-surface)',
            color: inCart > 0 ? 'var(--color-bg)' : 'var(--color-primary)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            transition: 'transform 120ms ease',
            cursor: 'pointer',
          }}
          onPointerDown={(e) => {
            e.currentTarget.style.transform = 'scale(0.92)';
          }}
          onPointerUp={(e) => (e.currentTarget.style.transform = 'scale(1)')}
          onPointerLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
        >
          {inCart > 0 ? (
            <span className="num" style={{ fontWeight: 600, fontSize: 14 }}>{inCart}</span>
          ) : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 5v14M5 12h14" />
            </svg>
          )}
        </button>
      </div>
    </article>
  );
}
